import { Button, Card, Col, Flex, Form, FormProps, Row } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

//components
import BackButton from 'components/backButton/backButton';
import PageTitle from 'components/title/title';
import ComponentHeader from 'components/componentHeader/componentHeader';
import FormElement from 'components/formElement/formElement';
import FormListFilesElement from 'components/documentsListForm/documentsListForm';

// services
import { onCreateBoardDocument, onGetBoardDocumentById, onUpdateBoardDocument } from '../services/documentsServices';
import NotificationService from 'core/services/notification';
import { checkEnglishValidation } from 'utils/validators/english';
import { checkArabicValidation } from 'utils/validators/arabic';

//models
import { CreateBoardDocumentPayload } from '../models/documents';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { TranslatedField } from 'core/models/common';
import { onEditMemberFromBoard } from '../services/memberServices';

//styles

interface BoardDocumentEditPageProps {}

const BoardDocumentEditPage: FunctionComponent<BoardDocumentEditPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { id } = useParams<{ id: string }>();

	const [documentForm] = Form.useForm();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	const deletedDocumentsIds: number[] = [];

	useEffect(() => {
		getDocument();
	}, []);

	useEffect(() => {}, [documentForm.getFieldsValue()]);

	const validateArabic = (_: any, value: string) => {
		if (!value) return Promise.resolve();
		if (checkArabicValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ARABIC_ONLY')));
		else return Promise.reject();
	};

	const validateEnglish = (_: any, value: string) => {
		if (!value) return Promise.resolve();
		if (checkEnglishValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ENGLISH_ONLY')));
		else return Promise.reject();
	};

	const getDocument = async () => {
		try {
			setIsLoading(true);

			const response = await onGetBoardDocumentById(id as string);

			documentForm.setFieldsValue({
				'title[ar]': response.translated_title.ar,
				'title[en]': response.translated_title.en,
				'description[ar]': response.translated_description.ar,
				'description[en]': response.translated_description.en,
				files: response.files,
			});

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			navigate('/dashboard/actions/boards/documents');
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const onFinish: FormProps<CreateBoardDocumentPayload>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const payload = new FormData();
			payload.append('_method', 'PUT');

			Object.entries(values).forEach(([key, value]) => {
				if (key != 'files') payload.append(key, value as string | Blob);
			});

			documentForm.getFieldValue('files')?.forEach((fileObj: any,index:number) => {
				if (fileObj.file) payload.append(`files[${index}]`, fileObj.file);
			});

			deletedDocumentsIds.forEach((id) => {
				payload.append('delete_file_ids[]', id.toString());
			});

			await onUpdateBoardDocument(id!, payload);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.DOCUMENT_UPDATED_SUCCESSFULLY'));

			navigate('/dashboard/actions/boards/documents');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), error?.response?.data?.message || t('COMMON.SUBMIT_ERROR'));
		}
	};

	return (
		<>
			<PageTitle name={t('PAGES.BOARD_MEETING')} />

			<BackButton />

			<Card>
				<ComponentHeader title={t('ACTIONS.UPDATE_BOARD_DOCUMENT')} />

				{isLoading ? (
					<LoadingComponent />
				) : (
					<Form form={documentForm} onFinish={onFinish} className="mt-5" initialValues={{ files: [{}] }}>
						<Row gutter={[16, 16]}>
							<Col span={24} md={12} className="mb-3">
								<FormElement
									name="title[ar]"
									type="text"
									label={t('ACTIONS.TITLE_IN_AR')}
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateArabic }]}
								/>
							</Col>

							<Col span={24} md={12}>
								<FormElement
									name="title[en]"
									type="text"
									label={t('ACTIONS.TITLE_IN_EN')}
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateEnglish }]}
								/>
							</Col>

							<Col span={24} md={12} className="mb-3">
								<FormElement
									name="description[ar]"
									type="text"
									label={t('ACTIONS.DESCRIPTION_IN_ARABIC')}
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateArabic }]}
								/>
							</Col>

							<Col span={24} md={12}>
								<FormElement
									name="description[en]"
									type="text"
									label={t('ACTIONS.DESCRIPTION_IN_ENGLISH')}
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateEnglish }]}
								/>
							</Col>

							<Col span={24}>
								<FormListFilesElement
									fieldName="files"
									form={documentForm}
									withTitle={false}
									editMode
									onDelete={(id: number) => (id ? deletedDocumentsIds.push(id) : null)}
								/>
							</Col>

							<Col span={24} className="mt-5">
								<Flex justify="end">
									<Button type="primary" htmlType="submit" loading={isSubmitting} disabled={isSubmitting}>
										{t('COMMON.UPDATE')}
									</Button>
								</Flex>
							</Col>
						</Row>
					</Form>
				)}
			</Card>
		</>
	);
};

export default BoardDocumentEditPage;
